
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AppRole } from "@/types/database/auth";

interface PasswordSetupFormProps {
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: AppRole;
  isLoading: boolean;
  onSubmit: (values: { password: string }) => Promise<void>;
}

const PasswordSetupForm = ({ 
  email, 
  firstName, 
  lastName, 
  phone, 
  role, 
  isLoading, 
  onSubmit 
}: PasswordSetupFormProps) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    await onSubmit({ password });
  };

  // Format role for display by capitalizing first letter
  const formatRole = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={email}
          disabled
          className="mt-1 bg-gray-50"
        />
      </div>

      {firstName && (
        <div>
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            type="text"
            value={firstName}
            disabled
            className="mt-1 bg-gray-50"
          />
        </div>
      )}

      {lastName && (
        <div>
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            type="text"
            value={lastName}
            disabled
            className="mt-1 bg-gray-50"
          />
        </div>
      )}

      {phone && (
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            disabled
            className="mt-1 bg-gray-50"
          />
        </div>
      )}

      {role && (
        <div>
          <Label htmlFor="role">Role</Label>
          <Input
            id="role"
            type="text"
            value={formatRole(role)}
            disabled
            className="mt-1 bg-gray-50"
          />
        </div>
      )}

      <div>
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="mt-1"
          placeholder="Enter your password"
        />
      </div>

      <div>
        <Label htmlFor="confirmPassword">Confirm Password</Label>
        <Input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          className="mt-1"
          placeholder="Confirm your password"
        />
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Setting up your account..." : "Set Password"}
      </Button>
    </form>
  );
};

export default PasswordSetupForm;
